import React, { useMemo } from 'react'

import { useInView } from 'react-cool-inview'

import { Button } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { string, bool } from 'prop-types'

import i18n from 'Pods/Shared/I18nHome'

import CustomSolutionCard from './Components/CustomSolutions/CustomSolutionCard'
import ExternalLink from './Icons/ExternalLink'
import Moderly from './Icons/Moderly'
import ThreeDJobs from './Icons/ThreeDJobs'
import ThreeDSolutionsIcon from './Icons/ThreeDSolutionsIcon'
import {
  getModerlyFeatures,
  getModerlySteps,
  getThreeDJobsFeatures,
  getThreeDJobsSteps,
} from './Static/customSolutionData'

function CustomSolutions({ locale = 'en', extractingCoreStyles = false }) {
  const { observe, inView } = useInView({
    rootMargin: '200px',
    unobserveOnEnter: true,
  })

  const translate = (key, options = {}) => {
    const result = i18n.t(key, {
      ns: 'new-home',
      lng: locale,
      ...options,
    })
    return result
  }

  const modelrySteps = getModerlySteps(locale)
  const modelryFeatures = getModerlyFeatures(locale)
  const customSolutionsSteps = getThreeDJobsSteps(locale)
  const customSolutionsFeatures = getThreeDJobsFeatures(locale)

  const ModelryButton = useMemo(() => {
    const buttonText = translate('customSolutions.modelry.buttonText')
    return function ModelryButtonComponent() {
      return (
        <Button
          customClass="modelry-button text-white"
          type="secondary"
          size="medium"
          href="https://www.modelry.ai/how-to-order-custom-3d-models"
          trailIcon={<ExternalLink width="16" height="16" />}
          data-track="custom-solution-click"
          data-solution-type="modelry"
          data-content-type="custom-solution"
        >
          {buttonText}
        </Button>
      )
    }
  }, [locale])

  const ThreeDJobsButton = useMemo(() => {
    const buttonText = translate('customSolutions.threeDJobs.buttonText')
    return function ThreeDJobsButtonComponent() {
      return (
        <Button
          customClass="three-d-jobs-button text-white"
          type="secondary"
          size="medium"
          href="/3d-modeling-jobs/new"
          trailIcon={<ExternalLink width="16" height="16" />}
          data-track="custom-solution-click"
          data-solution-type="3d-jobs"
          data-content-type="custom-solution"
        >
          {buttonText}
        </Button>
      )
    }
  }, [locale])

  const renderCustomSolutionsContent = () => (
    <div className="custom-solutions__container">
      <div className="custom-solutions__header text-center">
        <div className="custom-solutions__badge">
          <ThreeDSolutionsIcon className="custom-solutions__badge-icon" />
          <span className="text-light-green font-weight-600 uppercase cgt-legacy-text-default">
            {translate('customSolutions.badge')}
          </span>
        </div>
        <h2 className="custom-solutions__title text-white font-weight-600 text-center">
          {translate('customSolutions.title')}
        </h2>
      </div>

      <div className="custom-solutions__cards">
        <CustomSolutionCard
          stepsTitle={translate('customSolutions.stepsTitle')}
          steps={modelrySteps}
          features={modelryFeatures}
          featureTitle={translate('customSolutions.modelry.featureTitle')}
          cardTitle={translate('customSolutions.modelry.title')}
          cardDescription={translate('customSolutions.modelry.description')}
          Icon={Moderly}
          Button={ModelryButton}
        />
        <CustomSolutionCard
          stepsTitle={translate('customSolutions.stepsTitle')}
          steps={customSolutionsSteps}
          features={customSolutionsFeatures}
          cardTitle={translate('customSolutions.threeDJobs.title')}
          cardDescription={translate(
            'customSolutions.threeDJobs.description'
          )}
          featureTitle={translate('customSolutions.threeDJobs.featureTitle')}
          Icon={ThreeDJobs}
          Button={ThreeDJobsButton}
        />
      </div>
    </div>
  )

  return (
    <section className="custom-solutions" ref={observe}>
      {(inView || extractingCoreStyles) && renderCustomSolutionsContent()}
      <noscript>
        {renderCustomSolutionsContent()}
      </noscript>
    </section>
  )
}

CustomSolutions.propTypes = {
  locale: string,
  extractingCoreStyles: bool,
}

export default CustomSolutions
